import {useState} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {connect} from 'react-redux'

import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import AvatarGroup from '@mui/material/AvatarGroup'
import ListItemAvatar from '@mui/material/ListItemAvatar'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'

import CloseIcon from '@mui/icons-material/Close'

import EventFormPerformersAutocomplete from './EventFormPerformersAutocomplete'


const styles = {
  root: css`
    display: flex;
    flex-direction: column;
  `,
  performersHeader: css`
    margin-bottom: 20px;
    margin-top: 25px;
    h3 {
      margin-top: 0px;
      margin-bottom: 0px;
      font-size: 1.2rem;
      font-weight: 500;
    }
    div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
    }
  `,
  caption: css`
    font-size: 0.9rem;
    color: #636363;
    font-weight: 500;
  `,
  dialogTitle: css`
    font-weight: 500;
    font-size: 1.2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #000;
    color: #fff;
  `,
  inviteList: css`
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    min-height: 300px;
    max-height: 70vh;
    height: 600px;
    gap: 15px;
    > div {
      display: flex;
      align-items: center;
      gap: 10px;
      h2 {
        flex-grow: 1;
        margin: 0;
        font-size: 1.1rem;
      }
    }
  `,
  inviteButton: css`
    margin: 0;
    font-size: 0.8rem;
    font-weight: 500;
    text-transform: uppercase;
    box-shadow: none;
    &:hover {
      box-shadow: none;
    }
  `,
  performerItem: css`
    margin-top: 15px;
    margin-bottom: 15px;
    padding-left: 12px;
    padding-right: 12px;
  `,
  performerItemName: css`
    font-weight: 500;
    font-size: 1.1rem;
  `
}



const pastelColors = [
  '#FFCDD2', '#F8BBD0', '#E1BEE7', '#D1C4E9',
  '#C5CAE9', '#BBDEFB', '#B3E5FC', '#B2EBF2',
  '#B2DFDB', '#C8E6C9', '#DCEDC8', '#F0F4C3',
  '#FFF9C4', '#FFECB3', '#FFE0B2', '#FFDAB9'
]


function hashCode(str) {
  return Array.from(str).reduce((h, c) => h + c.charCodeAt(0), 0)
}
function getColor(id) {
  return pastelColors[hashCode(id) % pastelColors.length]
}
function initials(name) {
  name.split(' ').map(part => part.charAt(0)).join('').toUpperCase()
}



const EventFormLineup = ({
  performers,
  creatorID,
  creatorName,
  creatorType, 
  creatorAvatar,  
  setFieldValue,
}) => {
  
  const [inviteOpen, setInviteOpen] = useState(false)


  function open() {
    setInviteOpen(true)
  }


  function close() {
    setInviteOpen(false)
  }


  const removePerformer = (performer) => (e) => {
    e.stopPropagation()
    setFieldValue('performers', performers.filter(p => p.id !== performer.id))
  }


  return (
    <div css={styles.root}>
      <div css={styles.performersHeader}>
        <div>
          <h3>Lineup</h3>
          <Button css={styles.inviteButton} onClick={open}>
            Add | Invite
          </Button>
        </div>

        <p css={styles.caption}>
          You can invite performers later.
        </p>
        <p css={styles.caption}>
          --
        </p>
        <p css={styles.caption}>
          Artsts and bands that you invite to perform at this event will be sent an invitation
          that they need to accept to be listed in the lineup.
        </p>
      </div>

      <AvatarGroup max={5} sx={{justifyContent: 'flex-end', mb: 2}}>
        {creatorType === 'artist' && (
          <Avatar
            key={creatorID}
            alt={creatorName}
            src={creatorAvatar ?? undefined}
            sx={{backgroundColor: getColor(creatorID), fontSize: '0.7rem'}}
          >
            {/* fallback to first letter of name */}
            {!creatorAvatar && 'YOU'}
          </Avatar>
        )}

        {performers.map(p => {
          return (
            <Avatar
              key={p.id}
              alt={p.name}
              src={p.image ?? undefined}
              css={styles.avatar}
              sx={{backgroundColor: getColor(p.id)}}
            >
              {/* if p.image is falsy, show initials */}
              {!p.image && initials(p.name)}
            </Avatar>
          )
        })}
      </AvatarGroup>


      <Dialog open={inviteOpen} onClose={close} fullWidth maxWidth='xs'>
        <DialogTitle css={styles.dialogTitle}>
          Invite Performers
          <IconButton edge='end' onClick={close} sx={{mr: 1}}>
            <CloseIcon color='secondary' />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <EventFormPerformersAutocomplete
            performers={performers}
            setFieldValue={setFieldValue}
            label='Performer Search'
          />

          <div css={styles.inviteList}>
            {performers.map(performer => (
              <div key={performer.id} css={styles.performerItem}>
                <ListItemAvatar>
                  <Avatar
                    key={performer.id}
                    alt={performer.name}
                    src={performer.image ?? undefined}
                    css={styles.avatar}
                    sx={{backgroundColor: getColor(performer.id)}}
                  >
                   {/* if p.image is falsy, show initials */}
                    {!performer.image && initials(performer.name)}
                  </Avatar>
                </ListItemAvatar>
                <h2 css={styles.performerItemName}>{performer.name}</h2>
                <IconButton onClick={removePerformer(performer)}>
                  <CloseIcon />
                </IconButton>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}


const mapStateToProps = (state) => {
  const id = state.user?.account?.id
  const attributes = state.user?.account?.attributes
  return {
    creatorID: id,
    creatorName: attributes.name,
    creatorAvatar: attributes.imageDerivatives.small || attributes.externalImageUrl
  }
}


export default connect(mapStateToProps)(EventFormLineup)




